/**
 * The analysis board's move dispatcher (fix F-C / C2) — what a drag-drop on
 * the board, or an `EngineLines` row's press, does to the branch.
 *
 * Both entry points read BOTH stores fresh with `getState()`, never a
 * subscription: the board hands this straight to `Chessboard`'s
 * `onPieceDropExtension` (`stage/BoardFrame/model.ts`), so it has to have a
 * stable identity, and it only ever runs while `analysisStore.on` — a live
 * game never calls it (`actions.move` still owns that board).
 */
import {useLiveGame} from '@/Store/liveGameStore';

import {useV2Ui} from '../../theme/useV2Ui';
import {selectBranchTip, useAnalysis} from './analysisStore';
import type {BranchMove} from './analysisStore';
import {applyMove, isMainlineNext, sanToMove} from './branch';
import {analysisFenAtPly, analysisMoves, analysisPly} from './useEngine';

/**
 * Where the next branch move would start from: the existing branch's own
 * root, unchanged, or — with no branch yet — the position and ply the
 * notation is currently browsing. `tipFen` is the position the move is
 * actually played FROM (the branch's tip, or that same base position).
 */
const branchRoot = (): {baseFen: string; basePly: number; tipFen: string} => {
  const branch = useAnalysis.getState().branch;
  if (branch) {
    return {
      baseFen: branch.baseFen,
      basePly: branch.basePly,
      tipFen: selectBranchTip(useAnalysis.getState()) ?? branch.baseFen,
    };
  }
  const live = useLiveGame.getState();
  const viewPly = useV2Ui.getState().viewPly;
  const fen = analysisFenAtPly(live, viewPly);
  return {baseFen: fen, basePly: analysisPly(live, viewPly), tipFen: fen};
};

/**
 * The shared push rule (C2): with NO branch open, the main line's own next
 * move just advances the browsed ply instead of forking it; anything else —
 * including every move once a branch exists — starts or continues it.
 */
const land = (
  baseFen: string,
  basePly: number,
  move: BranchMove | null,
): void => {
  if (!move) {
    // the board only offers legal moves, but the dispatcher guards anyway
    return;
  }
  if (!useAnalysis.getState().branch) {
    const moves = analysisMoves(useLiveGame.getState());
    if (isMainlineNext(move.san, moves, basePly)) {
      const total = moves.length;
      useV2Ui.getState().setViewPly(basePly + 1 >= total ? null : basePly + 1);
      return;
    }
  }
  useAnalysis.getState().pushBranchMove(baseFen, basePly, move);
};

/** a drag-drop (or click-click) on the analysis board. */
export function analysisMove(
  from: string,
  to: string,
  promotion?: string,
): void {
  if (!useAnalysis.getState().on) {
    return;
  }
  const {baseFen, basePly, tipFen} = branchRoot();
  land(baseFen, basePly, applyMove(tipFen, from, to, promotion));
}

/** the first move of an `EngineLines` row, pressed. */
export function playEngineLine(san: string): void {
  if (!useAnalysis.getState().on) {
    return;
  }
  const {baseFen, basePly, tipFen} = branchRoot();
  land(baseFen, basePly, sanToMove(tipFen, san));
}

export default analysisMove;
